import { apiClient } from './client';
import { MarketKPIs, Continent, Country, Market } from '../../types';

export const marketsApi = {
  // Get all continents (reuse existing store endpoint)
  getContinents: async (): Promise<{ continents: Continent[] }> => {
    const response = await apiClient.get<any>('/api/store/markets/continents');

    // Map the API response to the expected format
    const continents = response.data?.map((continent: any) => ({
      id: continent.id,
      name: continent.name,
      code: continent.code,
      is_active: continent.isActive,
      active_markets: continent.activeMarketCount,
      active_countries: continent.activeCountryCount,
      total_markets: continent.totalMarketCount,
      total_countries: continent.totalCountryCount
    })) || [];

    return { continents };
  },

  // Get countries for a continent (reuse existing store endpoint)
  getCountries: async (continentId: number): Promise<{ countries: Country[] }> => {
    const response = await apiClient.get<any>(`/api/store/markets/countries/${continentId}`);

    // Map the API response to the expected format
    const countries = response.data?.map((country: any) => ({
      id: country.id,
      name: country.name,
      code: country.code,
      continent_id: country.continentId,
      is_active: country.isActive,
      active_markets: country.activeMarketCount,
      total_markets: country.totalMarketCount
    })) || [];

    return { countries };
  },

  // Get markets for a country (reuse existing store endpoint)
  getMarkets: async (countryId: number): Promise<{ markets: Market[]; continentId?: number; countryName?: string }> => {
    const response = await apiClient.get<any>(`/api/store/markets/country/${countryId}`);

    // Map the API response to the expected format
    const markets = response.data?.map((market: any) => ({
      id: market.id,
      name: market.name,
      slug: market.slug,
      country_id: market.countryId,
      is_active: market.isActive,
      product_image: market.productImage,
      product_image2: market.productImage2,
      product_image3: market.productImage3,
      sort_order: market.sortOrder
    })) || [];

    return {
      markets,
      continentId: response.metadata?.continent?.id,
      countryName: response.metadata?.country?.name
    };
  },

  // Get market KPIs (mobile-specific endpoint)
  getMarketKPIs: async (marketId: number, slice: string = 'All'): Promise<MarketKPIs> => {
    return apiClient.get(`/api/mobile/markets/${marketId}/kpis?slice=${encodeURIComponent(slice)}`);
  },

  // Get KPI detail timeseries
  getKPIDetail: async (
    marketId: number,
    kpiType: 'occupancy' | 'adr' | 'revpar' | 'rooms' | 'demand',
    years: number = 5,
    classification: string = 'All'
  ): Promise<any> => {
    return apiClient.get(`/api/mobile/markets/${marketId}/kpis/${kpiType}?years=${years}&slice=${encodeURIComponent(classification)}`);
  },

  // Get airport detail timeseries
  getAirportDetail: async (
    marketId: number,
    airportCode: string,
    years: number = 5
  ): Promise<any> => {
    return apiClient.get(`/api/mobile/markets/${marketId}/airports/${airportCode}?years=${years}`);
  },

  // Search markets (mobile-specific endpoint)
  searchMarkets: async (query: string): Promise<{ markets: Market[] }> => {
    return apiClient.get(`/api/mobile/search?q=${encodeURIComponent(query)}`);
  },
};
